/**
 * Gather — the hold-to-charge verb that drives `sim/attract.ts` (issue #122).
 *
 * Holding roots the player and opens a field that pulls loose cells toward
 * them. Radius grows with hold time in authored tiers; the field takes what
 * reaches the feet; releasing ends the root. **Cancelling by release is free.**
 * The cost is not a resource and not a cooldown: it is the seconds spent
 * standing still, which is a real cost *in this game specifically* — fire
 * spreads, gas rises, corpses keep burning, and every one of those is a system
 * that keeps running while you do not move.
 *
 * ## The charge is a `ChargeState`, on purpose
 *
 * `GATHER_TIERS` below is a `ChargeState[]`, the same type `WeaponDef.charge-
 * States` uses and the same one `releaseCharge` walks in `wielder.ts`. That is
 * reuse for a specific payoff: charged attacks already have a wind-up tell, and
 * a growing field that reads as a charge inherits it rather than inventing a
 * second visual language for "this is getting bigger the longer you hold". It
 * also means the tiers are *data* — a table a designer can read next to the
 * weapon tables — instead of a ladder of `if (ms > 700)`.
 *
 * The type carries `damageMul`, and **every tier here pins it to exactly 1**.
 * That is not an accident of a field being unused; it is the exclusion argued at
 * length in `sim/attract.ts`'s header, written where a future editor will
 * actually be standing when they decide the field feels weak and reach for a
 * number to raise. The one-sentence version:
 *
 *   **Damaging terrain creates loose material, so a gathering tool that damages
 *   manufactures its own supply, and every material sink in the game becomes
 *   free.**
 *
 * `tests/attraction.test.ts` fails if any tier's `damageMul` moves off 1.
 *
 * ## What this does to `combat/harvest.ts`: composes, does not replace
 *
 * The scythe keeps its harvest, unchanged. The two are different verbs against
 * different matter and the overlap is deliberate rather than redundant:
 *
 *   - `harvestSwing` takes cells **a swing was already going to destroy** — it
 *     is gated on `TerrainInteraction.destroy`, so it pockets rock the weapon
 *     was breaking anyway. It is a *by-product of attacking*, which is why it
 *     can be class-locked without stranding anyone: it costs no extra time.
 *   - This takes cells **already lying loose in the world**, and never breaks
 *     anything to make them. It is available to every build, because it is the
 *     economy's income side rather than one class's identity, and it is priced
 *     in the only currency a roguelite always has: standing still.
 *
 * Both funnel through `harvestMaterial`, so the pouch cap, the per-material
 * stack cap and the "refuse before the world does" ordering are enforced once
 * for both. The scythe's line in `weaponData.ts` is untouched: "harvests corpses
 * into pooled BLOOD" stays true and stays the *only* way to take matter out of
 * something you had to cut open first.
 *
 * ## Which half of a corpse each verb owns (#131), measured
 *
 * The overlap above was stated as a difference of *kind* — a by-product of
 * attacking versus a thing you stand still for. #131 asked which of them owns a
 * dead enemy, on the belief that this field refuses corpses outright. It does
 * not, and the measurement (`tests/corpseGather.test.ts`) makes the division
 * sharper than "corpses versus ambient debris":
 *
 *     the corpse   `corpseMaterial`, `volume` cells of the body's fluid or soft
 *                  part — soft for 50 of the 54 enemies, because
 *                  `corpseMaterial` scores the soft categories a point above
 *                  everything else. **This field's.**
 *     the bones    `corpseDebris`, `0.4 * volume` cells filtered to
 *                  `Category.RIGID` at source — rigid for 39 of the 44 enemies
 *                  that leave any. **`harvestSwing`'s**, and priced in hardness:
 *                  the bare scythe cuts to 3, BONE and STEEL and CORPSE_STONE
 *                  need a module.
 *     and cold     kill the same enemy frozen and `killEnemy` writes the
 *                  debris' `shatteredInto` — grit, gatherable for 42 of the 44.
 *                  **The bridge between the two**, and it is the same
 *                  `hasStatus(FROZEN)` the `rime` rite and `shatterFrozen`
 *                  already turn on.
 *
 * So a player who never carries a scythe still earns from every kill, a scythe
 * build earns the structural half as well, and freezing a construct is a third
 * route to the same matter that costs a status instead of a weapon slot. None of
 * that needed a new material or a wider filter; it needed writing down.
 */

import { clamp } from '../core/math';
import type { Input } from '../core/input';
import type { World } from '../entity/ecs';
import type { MoverState } from '../entity/movement';
import { gripMeansGather } from '../entity/abilities';
import { harvestMaterial, type MaterialPouch } from '../meta/crafting';
import { recordGathered, type MaterialLedger } from '../meta/runState';
import { ATTRACT_MAX_RADIUS, type AttractField } from '../sim/attract';
import { VOID, type MatId } from '../sim/materials';
import { materialName } from './harvest';
import type { ChargeState } from './types';
import { TETHER_MASS_RATIO } from './wielder';

/**
 * The hold ladder. Radii are `radiusMul * ATTRACT_MAX_RADIUS`, so the ceiling
 * moves with the work quota and never independently of it.
 *
 * `damageMul: 1` on every row. See the header — that is the decision, not a
 * default.
 *
 * The hold times are the same order as a weapon's: the first tier is instant so
 * a tap does something (a verb that needs 300 ms before it does anything reads
 * as a dropped input), and the top is a little over a second, which is a long
 * time to be standing in a room that is on fire.
 */
export const GATHER_TIERS: readonly ChargeState[] = [
  { id: 'gather.reach', holdMs: 0, damageMul: 1, radiusMul: 0.28 },
  { id: 'gather.draw', holdMs: 320, damageMul: 1, radiusMul: 0.5 },
  { id: 'gather.haul', holdMs: 700, damageMul: 1, radiusMul: 0.78 },
  { id: 'gather.maw', holdMs: 1150, damageMul: 1, radiusMul: 1 },
];

/**
 * Speed the mass rule hauls the player at, units/sec.
 *
 * Deliberately under 200. `pickEntryKind` promotes any attack press taken above
 * 200 u/s to the **dash** entry, and #50 measured what that does when a system
 * writes `w.vx[i]` for its own reasons: `tether_whip` landed 285 of 287 hits on
 * one node. A gathering field that flung its owner at 300 u/s would reproduce
 * that defect in a second system, from a completely different file, and it would
 * take another sweep to find. The root zeroes `vx` first, so this is the whole
 * of the resulting speed rather than an addition to it.
 */
const GATHER_HAUL = 150;

/** Vertical share of the haul, matching `GRAB_REEL`'s in `wielder.ts`. */
const GATHER_HAUL_RISE = 0.5;

export interface GatherCtx {
  field: AttractField;
  /** Where taken cells go. `RunState` satisfies it structurally. */
  pouch: MaterialPouch;
  /** Where taken cells are *counted*. `RunState` satisfies it too. */
  ledger: MaterialLedger;
  onEvent?: (msg: string) => void;
  /** Bodies the mass rule may consider. Same supplier as `AbilityCtx`. */
  forEachBody?: (fn: (idx: number) => void) => void;
}

export interface GatherState {
  /** Is the root open? */
  active: boolean;
  /**
   * Milliseconds the button has been held this hold. Named to match
   * `HandState.chargeHeld`, because it is the same quantity against the same
   * kind of table.
   */
  chargeHeld: number;
  /** Index into `GATHER_TIERS`; -1 while closed. */
  tier: number;
  /** Cells banked during the current hold, for the release line. */
  takenThisHold: number;
  /**
   * Materials already warned about this hold. Empty when nothing has been.
   *
   * **A set, and not the single slot this was.** The contract `warnHazard`
   * states is "once per material per hold", and one slot does not implement it:
   * it implements "once per *change* of nearest material". Those agree only
   * while there is one hazard. With two — a lava pool and an acid pool being
   * eaten at once, which is the ordinary case for a field at full radius — the
   * nearest cell flips every time one is collected, and the slot announces the
   * flip each time. Measured against the shipped code:
   * `tests/corpseGather.test.ts` drove twenty alternations and got **forty
   * lines** where the comment promised two.
   *
   * That is not cosmetic. The hazard line is the half of "you could see it
   * coming" that is not the HUD's job (#129 draws the tier pips and the named
   * warning; this writes the run log). A channel that prints forty lines during
   * one hold is a channel the player learns to skip, which is the same failure
   * as not warning at all, arrived at from the other side — the exact argument
   * the once-per-material rule was written down to avoid.
   *
   * Cleared rather than reallocated at `beginHold`, for the same reason `take`
   * is allocated once per hold: this runs on the movement substep.
   */
  readonly warned: Set<string>;
  /**
   * The mass rule fired and ended the last hold. Read by the tests and by
   * anything that wants to know why the root let go on its own.
   */
  hauled: boolean;
  /**
   * A hold the *field* ended, rather than the player. The button has to be
   * released before another one may open.
   *
   * Without it the mass rule is a no-op with a velocity spike attached: the haul
   * ends the root, the next substep sees the button still held and opens a fresh
   * hold, and that hold's first act is `w.vx[i] = 0` — so the player is rooted
   * again, one substep after being thrown, having moved about a pixel. The rule
   * would fire sixty times a second and never once be visible.
   */
  lockedUntilRelease: boolean;
  /** Allocated once per hold rather than per substep. */
  take: ((mat: MatId, x: number, y: number) => boolean) | null;
}

export function createGatherState(): GatherState {
  return {
    active: false,
    chargeHeld: 0,
    tier: -1,
    takenThisHold: 0,
    warned: new Set<string>(),
    hauled: false,
    lockedUntilRelease: false,
    take: null,
  };
}

/** Highest tier whose `holdMs` has elapsed. Never -1 once a hold is open. */
export function gatherTierAt(ms: number): number {
  let t = 0;
  for (let k = 0; k < GATHER_TIERS.length; k++) {
    if (ms >= GATHER_TIERS[k].holdMs) t = k;
  }
  return t;
}

/** Radius the field would open at, in cells, for a given tier index. */
export function gatherRadiusAt(tier: number): number {
  const t = GATHER_TIERS[clamp(tier, 0, GATHER_TIERS.length - 1) | 0];
  return ATTRACT_MAX_RADIUS * t.radiusMul;
}

/**
 * Advance the hold. Runs on the movement substep, after `updateAbilities`, so
 * that the sustained traversal states have already claimed the body and
 * `gripMeansGather` sees this substep's answer rather than last one's.
 *
 * The field itself is stepped once per *simulation* tick from
 * `core/game.ts:stepSimulation`; this only aims it. Two rates, one field, and
 * the split is the same one combat already runs on (docs/05 §7): the input
 * response has to be immediate, the cell work has to be budgeted.
 */
export function stepGather(
  state: GatherState,
  m: MoverState,
  w: World,
  i: number,
  input: Input | null,
  dt: number,
  ctx: GatherCtx,
): void {
  const wants = input !== null && input.held('grip') && gripMeansGather(m, w, i);
  if (!wants) {
    if (state.active) endHold(state, ctx, false);
    state.lockedUntilRelease = false;
    return;
  }
  if (state.lockedUntilRelease) return;

  if (!state.active) beginHold(state, ctx);
  state.chargeHeld += dt * 1000;

  const tier = gatherTierAt(state.chargeHeld);
  if (tier !== state.tier) {
    state.tier = tier;
    // The tell. A charged attack gets its wind-up drawn; this gets a line, and
    // the tier id is the same kind of token the weapon tiers use.
    if (tier > 0) ctx.onEvent?.(`field ${GATHER_TIERS[tier].id.split('.')[1]}`);
  }

  // The root. `vx` only: gravity is left alone on purpose, so a floor that burns
  // away underneath still drops you. An ability that suspended gravity here
  // would make standing in a fire *safer* than walking through it.
  w.vx[i] = 0;

  const radius = gatherRadiusAt(tier);
  ctx.field.open(w.x[i], w.y[i], radius, tier / (GATHER_TIERS.length - 1));

  warnHazard(state, ctx);

  // The mass rule, at full strength only — the field has to be a maw before it
  // can lose the argument with a boulder.
  if (tier === GATHER_TIERS.length - 1 && haulTowardAnchor(w, i, radius, ctx)) {
    endHold(state, ctx, true);
    state.lockedUntilRelease = true;
  }
}

function beginHold(state: GatherState, ctx: GatherCtx): void {
  state.active = true;
  state.chargeHeld = 0;
  state.tier = -1;
  state.takenThisHold = 0;
  state.warned.clear();
  state.hauled = false;
  state.take = (mat, _x, _y) => {
    const name = materialName(mat);
    if (name === undefined) return false;
    // The pouch refuses before the world does — `combat/harvest.ts`'s rule. A
    // full pouch leaves the cell standing rather than deleting matter that had
    // nowhere to go.
    if (harvestMaterial(ctx.pouch, name, 1) === 0) return false;
    recordGathered(ctx.ledger, 1);
    state.takenThisHold++;
    return true;
  };
  ctx.field.onArrive = state.take;
}

function endHold(state: GatherState, ctx: GatherCtx, hauled: boolean): void {
  ctx.field.close();
  ctx.field.onArrive = null;
  state.active = false;
  state.tier = -1;
  state.take = null;
  state.hauled = hauled;
  if (state.takenThisHold > 0) {
    // The one shipped reader of `RunStats.cellsGathered`. A counter with no
    // reader is the defect this project has now found fourteen times, and an
    // income ledger nobody is ever shown is exactly that shape.
    ctx.onEvent?.(`gathered ${state.takenThisHold} — ${ctx.ledger.stats.cellsGathered} this run`);
  }
  state.chargeHeld = 0;
}

/**
 * "It will vacuum lava, and that should stay."
 *
 * The field does not filter by material, and that is the decision: filtering is
 * the safe option and the boring one, and it would delete the only reason the
 * growing radius is a choice rather than "always hold to maximum". What makes it
 * a choice you got wrong rather than a gotcha is that the orange is visible
 * before it lands, and this is the half of that which is not a renderer's job:
 * one line the tick a hazard enters the field, while it is still out at the rim
 * with the better part of a second's crawl ahead of it.
 *
 * Once per material per hold. A per-tick line for a lava pool would be a
 * hundred lines and would train the player to ignore the log — which is the
 * same failure as not warning at all, arrived at from the other side.
 *
 * **The scope is the hold and the key is the material**, which is why
 * `GatherState.warned` is a set rather than a slot. `hazardMat` is *the nearest*
 * eligible hazard, so with two hazards in range it alternates as cells are
 * collected, and remembering only the last one announced turns "once per
 * material" into "once per flip" — forty lines over twenty alternations, in the
 * measurement on `warned`. Set membership is the contract stated directly.
 *
 * `field.hazardDist` is last tick's reading, because the field is stepped on the
 * simulation tick and this runs on the movement substep. That is the correct
 * staleness: the warning is *supposed* to describe matter that has not arrived.
 */
function warnHazard(state: GatherState, ctx: GatherCtx): void {
  const mat = ctx.field.hazardMat;
  if (mat === VOID) return;
  const name = materialName(mat);
  if (name === undefined || state.warned.has(name)) return;
  state.warned.add(name);
  ctx.onEvent?.(`${name.toLowerCase().replace(/_/g, ' ')} drawn into the field`);
}

/**
 * The tether's mass rule, applied to a field instead of to a rope.
 *
 * `TETHER_MASS_RATIO` already decides which end of a whip moves: a body at least
 * 2.5x the wielder's mass is an anchor, and `tether_whip` reels its *wielder*
 * toward it rather than dragging it in. A field at full strength beside
 * something that heavy loses the same argument for the same reason, so it
 * borrows the same constant rather than picking a second one that agrees until
 * one of them is tuned.
 *
 * It ends the root, which is the point: a hold that turns into a haul is the
 * field's own failure state, and standing rooted while being dragged would be
 * two systems writing the same position. The player is not punished beyond
 * losing the hold — everything already banked stays banked, because collection
 * happens on contact and not on release.
 *
 * Nearest qualifying body wins, so a boulder at arm's length beats one at the
 * rim; ties go to the first body the supplier visits, which is ECS row order and
 * therefore deterministic.
 */
function haulTowardAnchor(w: World, i: number, radius: number, ctx: GatherCtx): boolean {
  if (!ctx.forEachBody) return false;
  const anchorMass = w.mass[i] * TETHER_MASS_RATIO;
  const r2 = radius * radius;
  let best = -1;
  let bestD2 = Infinity;
  ctx.forEachBody((idx) => {
    if (idx === i) return;
    if (w.hp[idx] <= 0) return;
    if (w.mass[idx] < anchorMass) return;
    const dx = w.x[idx] - w.x[i];
    const dy = w.y[idx] - w.y[i];
    const d2 = dx * dx + dy * dy;
    if (d2 > r2 || d2 >= bestD2) return;
    bestD2 = d2;
    best = idx;
  });
  if (best < 0) return false;
  const d = Math.sqrt(bestD2) || 1;
  w.vx[i] = ((w.x[best] - w.x[i]) / d) * GATHER_HAUL;
  w.vy[i] = ((w.y[best] - w.y[i]) / d) * GATHER_HAUL * GATHER_HAUL_RISE;
  return true;
}
